import { useState } from "react";
import { Button, ErrorText, Field, Panel, SelectField, Spinner } from "../../components/ui";
import { fmtDate, fmtPrice } from "../../lib/format";
import { useSecurities } from "../securities/hooks";
import { usePriceHistory, useUpsertPrice } from "./hooks";
import type { ManualPriceInput } from "./api";

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = (): ManualPriceInput => ({ priceDate: today(), close: "", previousClose: "" });

export function PricesPage() {
  const securities = useSecurities();
  const [securityId, setSecurityId] = useState<string | null>(null);
  const history = usePriceHistory(securityId);
  const upsert = useUpsertPrice(securityId);
  const [form, setForm] = useState<ManualPriceInput>(emptyForm());

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!securityId) return;
    upsert.mutate(
      {
        priceDate: form.priceDate,
        close: form.close,
        previousClose: form.previousClose || null,
      },
      { onSuccess: () => setForm(emptyForm()) },
    );
  }

  return (
    <Panel title="Cours — saisie manuelle">
      <SelectField
        label="Titre"
        value={securityId ?? ""}
        onChange={(e) => setSecurityId(e.target.value || null)}
      >
        <option value="">— choisir —</option>
        {securities.data?.map((s) => (
          <option key={s.id} value={s.id}>
            {s.ticker} — {s.name}
          </option>
        ))}
      </SelectField>

      {securityId && (
        <>
          <form className="inline-form" onSubmit={submit}>
            <Field
              label="Date"
              type="date"
              value={form.priceDate}
              onChange={(e) => setForm({ ...form, priceDate: e.target.value })}
              required
            />
            <Field
              label="Cours (close)"
              value={form.close}
              onChange={(e) => setForm({ ...form, close: e.target.value })}
              required
            />
            <Field
              label="Clôture veille"
              value={form.previousClose ?? ""}
              onChange={(e) => setForm({ ...form, previousClose: e.target.value })}
            />
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? "…" : "Enregistrer"}
            </Button>
            <ErrorText error={upsert.error} />
          </form>

          <ErrorText error={history.error} />
          {history.isLoading ? (
            <Spinner />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clôture</th>
                    <th>Veille</th>
                    <th>Volume</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data?.map((p) => (
                    <tr key={p.id}>
                      <td>{fmtDate(p.priceDate)}</td>
                      <td>{fmtPrice(p.close)}</td>
                      <td>{fmtPrice(p.previousClose)}</td>
                      <td>{fmtPrice(p.volume, 0)}</td>
                      <td>
                        <span className={`tag tag-${p.source}`}>{p.source}</span>
                      </td>
                    </tr>
                  ))}
                  {history.data?.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted">
                        Aucun cours enregistré.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
